import { cn } from '@exam-room/utils';
import { Popover, PopoverButton, PopoverPanel, type PopoverPanelProps } from '@headlessui/react';
import { useDisclosure } from '@shared/hooks';
import { useRef, useState, useEffect, useCallback } from 'react';

import { ANCHOR_ORIGIN_MAP } from './constants';
import { type PlacementWitDashAlignment, type Origin, type Placement } from './types';
import { mapPlacement } from './utils';

export interface CopyToClipboardProps {
  text: string;
  className?: string;
  children?: React.ReactNode;
  slotProps?: {
    popoverPanel?: PopoverPanelProps & {
      hoverText?: string;
      successText?: string;
      duration?: number;
      placement?: `${Placement}` | `${PlacementWitDashAlignment}`;
    };
  };
}

export default function CopyToClipboard(props: Readonly<CopyToClipboardProps>) {
  const { text, className, children, slotProps = {} } = props;
  const {
    popoverPanel: {
      className: popoverPanelClassName,
      hoverText = 'Click to Copy',
      successText = 'Copied!',
      duration = 2000,
      placement = 'right',
      ...popoverPanelProps
    } = {},
  } = slotProps;

  const [actionText, setActionText] = useState<string>(hoverText);
  const [origin, setOrigin] = useState<Origin | 'origin-center'>('origin-left');
  const [isOpen, { open, close }] = useDisclosure(false);

  const popoverPanelRef = useRef<HTMLElement>(null);
  const timeOutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // Adjust transform-origin value based on the PopoverPanel's anchor data attribute
    const currentAnchor = popoverPanelRef.current?.dataset?.anchor;

    if (currentAnchor) {
      const originClassName = Object.keys(ANCHOR_ORIGIN_MAP).find((key) =>
        currentAnchor.includes(key),
      ) as Placement;

      setOrigin(ANCHOR_ORIGIN_MAP[originClassName] ?? 'origin-left');
    }
  }, [popoverPanelRef.current?.dataset?.anchor]);

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      try {
        event.stopPropagation();
        await navigator.clipboard.writeText(text);
        setActionText(successText);
        open();
      } catch (error) {
        console.error('Failed to copy text: ', error);
      } finally {
        clearTimeout(timeOutRef.current);
        timeOutRef.current = setTimeout(() => {
          close();
        }, duration);
      }
    },
    [text, open, close],
  );

  const handleMouseEnter = useCallback(() => {
    setActionText(hoverText);
    open();
  }, [open, hoverText]);

  const handleMouseLeave = useCallback(() => {
    close();
  }, [close]);

  return (
    <Popover>
      <PopoverButton
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={cn(`cursor-pointer focus:outline-0 active:outline-0`, className)}
      >
        {children}
      </PopoverButton>

      <PopoverPanel
        ref={popoverPanelRef}
        transition
        static
        anchor={{ to: mapPlacement(placement), gap: 16 }}
        className={cn(
          'flex w-fit scale-80 items-center rounded-sm bg-gray-700 px-3.5 py-2.5 text-xs text-white transition duration-150 ease-out',
          isOpen ? 'scale-100 opacity-100' : 'pointer-events-none opacity-0',
          origin,
          popoverPanelClassName,
        )}
        {...popoverPanelProps}
      >
        <span>{actionText}</span>
      </PopoverPanel>
    </Popover>
  );
}
